import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { addCustomerCall, reset } from "../../redux/slice/addCustomer";

import "./NewCustomer.scss";
import Layout from "../../component/layout/Layout";
import Spinner from "../../component/spinner/Spinner";
import Toast from "../../component/toast/Toast";
import { createUserGroupCall } from "../../redux/slice/createUserGroup";
import { joinMemberCall } from "../../redux/slice/joinMember";

function NewCustomer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addCustomerData, loading, success } = useSelector(
    (state) => state.addCustomer,
  );

  const { userGroupData } = useSelector((state) => state.userGroup);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobile, setMobile] = useState(localStorage.getItem("mobile"));

  // Store options based on environment
  const getStoreOptions = () => {
    const brand = process.env.REACT_APP_BRAND;
    const isDemo = brand === "DLFMallsDemo";
    const prefix = isDemo ? "dlf.demo" : "dlf.live";

    return [
      { label: "DLF Avenue", value: `${prefix}.avenue` },
      { label: "DLF Promenade", value: `${prefix}.promenade` },
      { label: "DLF Mall of India", value: `${prefix}.mall_of_india` },
      { label: "DLF Cyberhub", value: `${prefix}.cyberhub` },
      { label: "DLF Midtown Plaza", value: `${prefix}.midtown_plaza` },
      { label: "DLF Summit Plaza", value: `${prefix}.summit_plaza` },
    ];
  };

  let timerId = null;

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setFirstName(values.firstName);
    setLastName(values.lastName);
    setSubmitting(true);
    try {
      const obj = {
        root: {
          customer: [
            {
              mobile,
              firstname: values.firstName,
              lastname: values.lastName,
              extended_fields: {
                field: [
                  {
                    name: "preferred_store",
                    value: values.preferredStore,
                  },
                ],
              },
            },
          ],
        },
      };
      const result = await dispatch(addCustomerCall(obj));
      let userId = "";
      // If successfully registered go to login page
      if (result?.payload?.status?.success === ("true" || true)) {
        userId = result?.payload?.customers?.customer[0]?.user_id;
        const createGroupData = {
          externalId: mobile,
          groupName: mobile,
          maxGroupSize: 5,
        };
        // Create Group Api call
        const createGroupResult = await dispatch(
          createUserGroupCall(createGroupData),
        );

        if (createGroupResult?.payload?.warnings?.length === 0) {
          const data = [
            {
              userId: userId,
              primaryMember: true,
              permissions: [],
            },
          ];
          const entityId = createGroupResult?.payload?.entity;
          // Join Member Group Api call
          const joinMemberResult = await dispatch(
            joinMemberCall({ data, entityId }),
          );
        }
        timerId = setTimeout(() => {
          navigate("/home");
        }, 1200);
      }
    } catch (error) {
      setFieldError("mobile", "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    return () => {
      dispatch(reset());
      // Cleaning setTimeout
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    };
  }, []);

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "Only alphabetic characters, hyphens, apostrophes, and spaces are allowed",
      )
      .max(50, "Maximum 50 characters allowed")
      .required("First Name is required"),
    lastName: Yup.string()
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "Only alphabetic characters, hyphens, apostrophes, and spaces are allowed",
      )
      .max(50, "Maximum 50 characters allowed")
      .required("Last Name is required"),
    mobile: Yup.string().required("Mobile number is required"),
    preferredStore: Yup.string().required("Preferred Store is required"),
  });

  return (
    <>
      <Layout>
        <div className="mob_h1">
          <h1 className="mobile_content ">Welcome to </h1>
          <h1 className="mobile_content ">DLF Mall Of India</h1>
        </div>
        <div className="helpbox">
          <div className="inner_center_box">
            <label className="tophead">
              Please let us know if you are having trouble in getting the OTP
            </label>

            <Formik
              initialValues={{
                firstName: "",
                lastName: "",
                mobile: mobile || "",
                preferredStore: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form>
                  <div className="form-check_1">
                    <div className="div_center formik-field">
                      <Field
                        name="firstName"
                        type="text"
                        placeholder="First Name *"
                        className={`form-control mobile ${
                          errors.firstName && touched.firstName
                            ? "error-border"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="firstName"
                        component="div"
                        className="error-text"
                      />
                    </div>
                    <div className="div_center formik-field">
                      <Field
                        name="lastName"
                        type="text"
                        placeholder="Last Name *"
                        className={`form-control mobile ${
                          errors.lastName && touched.lastName
                            ? "error-border"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="lastName"
                        component="div"
                        className="error-text"
                      />
                    </div>
                    <div className="div_center">
                      <Field
                        name="mobile"
                        type="text"
                        placeholder="Mobile *"
                        className="form-control mobile"
                        readOnly
                      />
                      <ErrorMessage
                        name="mobile"
                        component="div"
                        className="error-text"
                      />
                    </div>
                    <div className="div_center formik-field">
                      <Field
                        name="preferredStore"
                        as="select"
                        className={`form-control mobile ${
                          errors.preferredStore && touched.preferredStore
                            ? "error-border"
                            : ""
                        }`}
                      >
                        <option value="">Select Preferred Store *</option>
                        {getStoreOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="preferredStore"
                        component="div"
                        className="error-text"
                      />
                    </div>

                    <button type="submit" disabled={isSubmitting}>
                      Submit {isSubmitting && <Spinner />}
                    </button>
                    <p>
                      <Link to="/login">
                        <span className="high_light_text">Back to Login</span>
                      </Link>
                    </p>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Layout>

      {/* <Toast
        flag={success}
        message={`${firstName} ${lastName} registered successfully`}
      /> */}
    </>
  );
}

export default NewCustomer;
